// tslint:disable:variable-name
import { HttpClient, HttpHeaderResponse, HttpParams, HttpProgressEvent, HttpResponse, HttpSentEvent, HttpUserEvent } from '@angular/common/http';
import { BehaviorSubject, Observable, of, Subscription } from 'rxjs';
import { catchError, finalize, tap } from 'rxjs/operators';
import { PaginatorState } from '../models/paginator.model';
import { ITableState, TableResponseModel } from '../models/table.model';
import { BaseModel } from '../models/base.model';
import { SortState } from '../models/sort.model';
import { GroupingState } from '../models/grouping.model';

const DEFAULT_STATE: ITableState = {
  filter: {},
  paginator: new PaginatorState(),
  sorting: new SortState(),
  searchTerm: '',
  grouping: new GroupingState(),
  entityId: undefined,
  tenantId: undefined
};

export abstract class TableService<T> {
  // Private fields
  protected _items$ = new BehaviorSubject<T[]>([]);
  protected _isLoading$ = new BehaviorSubject<boolean>(false);
  protected _isFirstLoading$ = new BehaviorSubject<boolean>(true);
  protected _tableState$ = new BehaviorSubject<ITableState>(DEFAULT_STATE);
  protected _errorMessage = new BehaviorSubject<string>('');
  protected _subscriptions: Subscription[] = [];
  protected _totalCount: number = 0;

  // Getters
  get items$() {
    return this._items$.asObservable();
  }
  get isLoading$() {
    return this._isLoading$.asObservable();
  }
  get isFirstLoading$() {
    return this._isFirstLoading$.asObservable();
  }
  get errorMessage$() {
    return this._errorMessage.asObservable();
  }
  get tableState$() {
    return this._tableState$.asObservable();
  }
  get subscriptions() {
    return this._subscriptions;
  }
  // State getters
  get paginator() {
    return this._tableState$.value.paginator;
  }
  get filter() {
    return this._tableState$.value.filter;
  }
  get sorting() {
    return this._tableState$.value.sorting;
  }
  get searchTerm() {
    return this._tableState$.value.searchTerm;
  }
  get grouping() {
    return this._tableState$.value.grouping;
  }
  get totalCount() {
    return this._totalCount;
  }
  get tableState() {
    return this._tableState$.value;
  }

  apiUrl!: string;

  constructor(protected http: HttpClient) {

  }

  setApiUrl(url: string) {
    this.apiUrl = url;
  }

  // Create
  create(item: BaseModel, method: string = '') {
    this._isLoading$.next(true);
    this._errorMessage.next('');

    let url = `${this.apiUrl}`;

    if (method)
      url = `${this.apiUrl}/${method}`;

    return this.http.post<BaseModel>(url, item).pipe(
      finalize(() => this._isLoading$.next(false))
    );
  }


  // READ (Returning filtered list of entities)
  find(tableState: ITableState, method: string = '') {
    let url = `${this.apiUrl}` + '/get';
    if (method)
      url = `${this.apiUrl}/${method}`;

    this._errorMessage.next('');
    return this.http.post<TableResponseModel<T>>(url, tableState).pipe(
      catchError(err => {
        this._errorMessage.next(err.error);
        console.error('FIND ITEMS', err);
        return of({ result: [], totalCount: 0 });
      })
    );
  }

  getItemById(id: any, method: string = ''): Observable<BaseModel |
    HttpSentEvent |
    HttpHeaderResponse |
    HttpProgressEvent |
    HttpResponse<BaseModel> |
    HttpUserEvent<BaseModel>> {
    this._isLoading$.next(true);
    this._errorMessage.next('');
    let url = `${this.apiUrl}/getById`;
    if (method)
      url = `${this.apiUrl}/${method}`;
    url += `?id=${id}`
    return this.http.get<BaseModel>(url).pipe(
      finalize(() => this._isLoading$.next(false))
    );
  }

  // UPDATE
  update(item: BaseModel, method: string = ''): Observable<any> {
    this._isLoading$.next(true);
    this._errorMessage.next('');
    let url = `${this.apiUrl}/update`;

    if (method)
      url = `${this.apiUrl}/${method}`;

    return this.http.post(url, item).pipe(
      finalize(() => this._isLoading$.next(false))
    );
  }

  // UPDATE Status
  updateStatusForItems(ids: number[], status: number): Observable<any> {
    this._isLoading$.next(true);
    this._errorMessage.next('');
    const body = { ids, status };
    const url = `${this.apiUrl}` + '/updateStatus';
    return this.http.put(url, body).pipe(
      catchError(err => {
        this._errorMessage.next(err.error);
        console.error('UPDATE STATUS FOR SELECTED ITEMS', ids, status, err);
        return of([]);
      }),
      finalize(() => this._isLoading$.next(false))
    );
  }

  // DELETE
  delete(id: any, tenantId: any = null): Observable<any> {
    this._isLoading$.next(true);
    this._errorMessage.next('');
    let params = new HttpParams();
    params = params.set("id", id);

    if (tenantId)
      params = params.append('tenantId', tenantId);

    return this.http.delete(`${this.apiUrl}/delete`, { params: params }).pipe(
      finalize(() => this._isLoading$.next(false))
    );
  }

  // SOFT DELETE
  softDelete(id: string) {
    this._isLoading$.next(true);
    this._errorMessage.next('');
    let params = new HttpParams();
    params = params.set("id", id);

    return this.http.delete(`${this.apiUrl}/softdelete`, { params: params }).pipe(
      finalize(() => this._isLoading$.next(false))
    );
  }

  // delete list of items
  deleteItems(ids: number[] = []): Observable<any> {
    this._isLoading$.next(true);
    this._errorMessage.next('');
    const url = `${this.apiUrl}` + '/BulkDelete';
    const body = { ids };
    return this.http.post(url, body).pipe(
      catchError(err => {
        this._errorMessage.next(err.error);
        console.error('DELETE SELECTED ITEMS', ids, err);
        return of([]);
      }),
      finalize(() => this._isLoading$.next(false))
    );
  }

  public fetch(method: string = '') {
    this._isLoading$.next(true);
    this._errorMessage.next('');
    const request = this.find(this._tableState$.value, method)
      .pipe(
        tap((res: TableResponseModel<T>) => {
          this._items$.next(res.result);
          this._totalCount = res.totalCount;
          this.patchStateWithoutFetch({
            paginator: this._tableState$.value.paginator.recalculatePaginator(
              res.totalCount
            ),
          });
        }),
        catchError((err) => {
          this._errorMessage.next(err.error);
          return of({
            items: [],
            total: 0
          });
        }),
        finalize(() => {
          this._isLoading$.next(false);
          const itemIds = this._items$.value.map((el: T) => {
            const item = (el as unknown) as BaseModel;
            return item.id;
          });
          this.patchStateWithoutFetch({
            grouping: this._tableState$.value.grouping.clearRows(itemIds),
          });
        })
      )
      .subscribe();
    this._subscriptions.push(request);
  }

  public get(method: string = '') {
    let url = `${this.apiUrl}`;
    if (method)
      url = `${this.apiUrl}/${method}`;

    this._isLoading$.next(true);
    this._errorMessage.next('');

    let params = new HttpParams();

    for (const [k, v] of Object.entries(this._tableState$.value)) {
      if (k == 'filter') {
        for (const [i, j] of Object.entries(this._tableState$.value.filter)) {
          if (j instanceof Array) {
            for (let p = 0; p < j.length; p++) {
              params = params.append(`filter.${i}[${p}]`, j[p]);
            }
          } else {
            params = params.append(`filter.${i}`, <any>j);
          }
        }
      } else if (k == 'paginator') {
        for (const [i, j] of Object.entries(this._tableState$.value.paginator)) {
          params = params.append(`paginator.${i}`, <any>j);
        }
      } else if (k == 'sorting') {
        for (const [i, j] of Object.entries(this._tableState$.value.sorting)) {
          params = params.append(`sorting.${i}`, <any>j);
        }
      } else if (k == 'grouping') {
        if (this._tableState$.value.grouping)
          for (const [i, j] of Object.entries(this._tableState$.value.grouping)) {
            if (j instanceof Array) {
              for (let p = 0; p < j.length; p++) {
                params = params.append(`grouping.${i}[${p}]`, j[p]);
              }
            } else if (j instanceof Set) {
              j.forEach(p => {
                params = params.append(`grouping.${i}[${p}]`, p);
              })
            } else {
              params = params.append(`grouping.${i}`, <any>j);
            }
          }
      } else {
        params = params.append(k, v);
      }
    }

    let request = this.http.get<TableResponseModel<T>>(url, { params: params }).pipe(
      tap((res: TableResponseModel<T>) => {
        this._items$.next(res.result);
        this._totalCount = res.totalCount;
        this.patchStateWithoutFetch({
          paginator: this._tableState$.value.paginator.recalculatePaginator(
            res.totalCount
          ),
        });
      }),
      catchError(err => {
        this._errorMessage.next(err.error);
        console.error('GET ITEMS', err);
        return of({ result: [], totalCount: 0 });
      }),
      finalize(() => {
        this._isLoading$.next(false);
        const itemIds = this._items$.value.map((el: T) => {
          const item = (el as unknown) as BaseModel;
          return item.id;
        });
        this.patchStateWithoutFetch({
          grouping: this._tableState$.value.grouping.clearRows(itemIds),
        });
      })
    ).subscribe();

    this._subscriptions.push(request);
  }

  public setDefaults() {
    this.patchStateWithoutFetch({ filter: {} });
    this.patchStateWithoutFetch({ sorting: new SortState() });
    this.patchStateWithoutFetch({ grouping: new GroupingState() });
    this.patchStateWithoutFetch({ searchTerm: '' });
    this.patchStateWithoutFetch({
      paginator: new PaginatorState()
    });
    this._isFirstLoading$.next(true);
    this._isLoading$.next(true);
    this._tableState$.next(DEFAULT_STATE);
    this._errorMessage.next('');
  }

  resetItems() {
    this._items$.next([]);
  }

  // Base Methods
  public patchState(patch: Partial<ITableState>, method = '') {
    this.patchStateWithoutFetch(patch);
    this.fetch(method);
  }

  public patchStateWithoutFetch(patch: Partial<ITableState>) {
    const newState = Object.assign(this._tableState$.value, patch);
    this._tableState$.next(newState);
  }
}
